/**
 * Helpers to resolve content-bank labels and categories via i18n with fallback to API/raw values.
 * Used by games that consume content from the backend content bank (Word Fluency Tap, N-Back, etc.).
 */

import type { TFunction } from "i18next";

const CONTENT_BANK_NS = "games.contentBank";

/** Item IDs from asset manifest follow pattern img_0001, img_0002, ... */
const CONTENT_BANK_ITEM_ID_RE = /^img_\d+$/;

export function isContentBankItemId(id: string): boolean {
  return typeof id === "string" && CONTENT_BANK_ITEM_ID_RE.test(id);
}

/**
 * Returns translated label for a content-bank item (asset), or fallback when key is missing.
 * Use when displaying item/tile labels that may come from the content bank (e.g. itemId from API).
 */
export function getContentBankLabel(
  t: TFunction,
  id: string,
  fallback: string
): string {
  if (!id || !isContentBankItemId(id)) return fallback;
  const key = `${CONTENT_BANK_NS}.assets.labels.${id}`;
  const out = t(key);
  return out && out !== key ? out : fallback;
}

/**
 * Returns translated label for a content-bank category slug, or fallback when key is missing.
 * Use for category prompts (e.g. "Tap all animals") when the category comes from the content bank.
 */
export function getContentBankCategoryLabel(
  t: TFunction,
  slug: string,
  fallback: string
): string {
  if (!slug || typeof slug !== "string") return fallback;
  const key = `${CONTENT_BANK_NS}.assets.categories.${slug}`;
  const out = t(key);
  return out && out !== key ? out : fallback;
}

/**
 * Returns the avatar voice-over sentence for a content-bank item, or
 * `null` if no localized description is available. Resolution order:
 *   1. `apiVoiceDescription` (when the backend item carries one)
 *   2. i18n key `games.contentBank.assets.voiceDescriptions.{itemId}`
 *   3. localized item label (so the avatar at least names the item)
 *   4. `fallback`
 *
 * Used by `GameAvatarOverlay` consumers (Category Tap, Word Fluency Tap)
 * to feed the avatar's `speak()` API.
 */
export function getContentBankVoiceDescription(
  t: TFunction,
  id: string,
  fallback: string,
  apiVoiceDescription?: string | null
): string | null {
  if (apiVoiceDescription && apiVoiceDescription.trim().length > 0) {
    return apiVoiceDescription;
  }
  if (id && isContentBankItemId(id)) {
    const key = `${CONTENT_BANK_NS}.assets.voiceDescriptions.${id}`;
    const out = t(key);
    if (out && out !== key) return out;
  }
  const label = getContentBankLabel(t, id, fallback);
  return label || null;
}

/** Rule Switch bin / object attribute slugs (matches ruleSwitchContent pools). */
export type RuleSwitchAttributeRule = "color" | "shape" | "size" | "pattern";

/**
 * Localized display string for a Rule Switch attribute value (e.g. colour name, shape).
 * Keys live under content-bank `ruleSwitch.{rule}.{slug}`.
 */
export function getRuleSwitchAttributeLabel(
  t: TFunction,
  rule: RuleSwitchAttributeRule,
  valueSlug: string
): string {
  if (!valueSlug) return valueSlug;
  const key = `${CONTENT_BANK_NS}.ruleSwitch.${rule}.${valueSlug}`;
  const out = t(key);
  return out && out !== key ? out : valueSlug;
}
